import { transformError as sdkTransformError } from '@wix/sdk-runtime/transform-error';
import { queryBuilder } from '@wix/sdk-runtime/query-builder';
import {
  renameKeysFromSDKRequestToRESTRequest,
  renameKeysFromRESTResponseToSDKResponse,
} from '@wix/sdk-runtime/rename-all-nested-keys';
import {
  HttpClient,
  HttpResponse,
  Search as SearchSdkType,
  NonNullablePaths,
  QuerySpec,
  Query as QuerySdkType,
  SearchSpec,
} from '@wix/sdk-types';
import * as ambassadorWixPaymentlinksPaymentsV1PaymentLinkPayment from './paymentlinks-payments-v1-payment-link-payment-payment-link-payments.http.js';
// @ts-ignore
import { transformRESTAddressToSDKAddress } from '@wix/sdk-runtime/transformations/address';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { createQueryUtils } from '@wix/sdk-runtime/query-builder-utils';
import { createSearchUtils } from '@wix/sdk-runtime/search-builder-utils';

/** A payment link payment contains details of a payment made using [payment links](https://support.wix.com/en/article/wix-pay-links-creating-and-managing-pay-links). */
export interface PaymentLinkPayment
  extends PaymentLinkPaymentPaymentOneOf,
    PaymentLinkPaymentEcomCheckoutTypeOneOf,
    PaymentLinkPaymentCheckoutTypeOneOf {
  /** Conventional payment, not including Wix eCommerce's gift cards. Includes payments made through Wix Payments, Stripe, and all [supported payment gateways](https://www.wix.com/payments/payment-gateways). */
  regularPaymentLinkPayment?: RegularPaymentLinkPayment;
  /** Wix eCommerce gift card payment details. */
  giftCardPaymentLinkPayment?: GiftCardPaymentLinkPayment;
  /**
   * Standard checkout.
   * @internal
   * @deprecated
   * @replacedBy checkout_type.ecom_checkout
   * @targetRemovalDate 2026-01-01
   */
  standardCheckout?: StandardCheckout;
  /**
   * Lean checkout.
   * @internal
   * @deprecated
   * @replacedBy checkout_type.ecom_order_checkout
   * @targetRemovalDate 2026-01-01
   */
  leanCheckout?: LeanCheckout;
  /** Wix eCommerce checkout. */
  ecomCheckout?: EcomCheckout;
  /** Wix eCommerce order checkout. */
  ecomOrderCheckout?: EcomOrderCheckout;
  /**
   * Payment link payment ID. When a gift card is used, this is the gift card payment ID.
   * Otherwise, this is the transaction ID associated with the relevant payment type.
   * @format GUID
   */
  _id?: string | null;
  /**
   * Revision number, which increments by 1 each time the payment link payment is updated.
   * @readonly
   */
  revision?: string | null;
  /**
   * Date and time the payment was created.
   * @readonly
   * @immutable
   */
  _createdDate?: Date | null;
  /**
   * Payment amount.
   * @format DECIMAL_VALUE
   * @decimalValue options { gte:0, lte:1000000000000000, maxScale:3 }
   */
  amount?: string;
  /**
   * Payment link ID associated with the payment.
   * @format GUID
   */
  paymentLinkId?: string;
  /**
   * Receipt ID associated with the payment, available in the Get Paid Receipts API.
   * @format GUID
   * @readonly
   */
  receiptId?: string | null;
  /** Payment type. */
  type?: PaymentLinkPaymentTypeWithLiterals;
  /**
   * Currency code in [ISO-4217 alphabetic](https://www.iso.org/iso-4217-currency-codes.html) format.
   * @internal
   * @format CURRENCY
   * @readonly
   * @deprecated
   * @replacedBy currency
   * @targetRemovalDate 2026-01-01
   */
  currencyCode?: string;
  /**
   * Currency code in [ISO-4217 alphabetic](https://www.iso.org/iso-4217-currency-codes.html) format.
   * @format CURRENCY
   * @readonly
   */
  currency?: string;
  /** Date and time the payment transaction was created. */
  transactionDate?: Date | null;
  /**
   * Custom field data for the payment link.
   * [Extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields) must be configured in the app dashboard before they can be accessed with API calls.
   */
  extendedFields?: ExtendedFields;
}

/** @oneof */
export interface PaymentLinkPaymentPaymentOneOf {
  /** Conventional payment, not including Wix eCommerce's gift cards. Includes payments made through Wix Payments, Stripe, and all [supported payment gateways](https://www.wix.com/payments/payment-gateways). */
  regularPaymentLinkPayment?: RegularPaymentLinkPayment;
  /** Wix eCommerce gift card payment details. */
  giftCardPaymentLinkPayment?: GiftCardPaymentLinkPayment;
}

/** @oneof */
export interface PaymentLinkPaymentEcomCheckoutTypeOneOf {
  /**
   * Standard checkout.
   * @internal
   * @deprecated
   * @replacedBy checkout_type.ecom_checkout
   * @targetRemovalDate 2026-01-01
   */
  standardCheckout?: StandardCheckout;
  /**
   * Lean checkout.
   * @internal
   * @deprecated
   * @replacedBy checkout_type.ecom_order_checkout
   * @targetRemovalDate 2026-01-01
   */
  leanCheckout?: LeanCheckout;
}

/** @oneof */
export interface PaymentLinkPaymentCheckoutTypeOneOf {
  /** Wix eCommerce checkout. */
  ecomCheckout?: EcomCheckout;
  /** Wix eCommerce order checkout. */
  ecomOrderCheckout?: EcomOrderCheckout;
}

export enum PaymentLinkPaymentType {
  /** Unknown payment type. */
  UNKNOWN_PAYMENT_TYPE = 'UNKNOWN_PAYMENT_TYPE',
  /** Conventional payment, not including Wix eCommerce's gift cards. Includes payments made through Wix Payments, Stripe, and all [supported payment gateways](https://www.wix.com/payments/payment-gateways). */
  REGULAR = 'REGULAR',
  /** Payment using a Wix eCommerce gift card. */
  GIFT_CARD = 'GIFT_CARD',
}

/** @enumType */
export type PaymentLinkPaymentTypeWithLiterals =
  | PaymentLinkPaymentType
  | 'UNKNOWN_PAYMENT_TYPE'
  | 'REGULAR'
  | 'GIFT_CARD';

export interface RegularPaymentLinkPayment
  extends RegularPaymentLinkPaymentPaymentMethodDataOneOf {
  /** Credit card payment method data. */
  creditCardPaymentMethodData?: CreditCardPaymentMethodData;
  /** Wallet payment method data. */
  walletPaymentMethodData?: WalletPaymentMethodData;
  /**
   * Payment method.
   * @readonly
   * @minLength 1
   * @maxLength 100
   */
  paymentMethod?: string;
  /**
   * Payment service provider.
   * @readonly
   * @minLength 1
   * @maxLength 100
   */
  paymentProvider?: string;
  /**
   * Buyer contact details.
   * @readonly
   */
  contactDetails?: FullAddressContactDetails;
  /**
   * Buyer contact ID, if relevant.
   * @format GUID
   * @readonly
   */
  contactId?: string | null;
  /**
   * Buyer's billing address.
   * @readonly
   */
  billingAddress?: Address;
  /** Payment method type. */
  method?: PaymentMethodWithLiterals;
  /**
   * Payment order.
   * @readonly
   */
  order?: PaymentOrder;
}

/** @oneof */
export interface RegularPaymentLinkPaymentPaymentMethodDataOneOf {
  /** Credit card payment method data. */
  creditCardPaymentMethodData?: CreditCardPaymentMethodData;
  /** Wallet payment method data. */
  walletPaymentMethodData?: WalletPaymentMethodData;
}

export interface FullAddressContactDetails {
  /** Contact's first name. */
  firstName?: string | null;
  /** Contact's last name. */
  lastName?: string | null;
  /**
   * Contact's full name.
   * @internal
   */
  fullName?: string | null;
  /**
   * Contact's phone number.
   * @format PHONE
   */
  phone?: string | null;
  /** Contact's company name. */
  company?: string | null;
  /**
   * Email associated with the address.
   * @format EMAIL
   */
  email?: string | null;
  /** Tax info. */
  vatId?: VatId;
}

export interface VatId {
  /**
   * Customer's tax ID.
   * @maxLength 100
   */
  _id?: string;
  /**
   * Tax type.
   *
   * Supported values:
   * + `CPF`: for individual tax payers
   * + `CNPJ`: for corporations
   */
  type?: VatTypeWithLiterals;
}

/** tax info types */
export enum VatType {
  UNSPECIFIED = 'UNSPECIFIED',
  /** CPF - for individual tax payers. */
  CPF = 'CPF',
  /** CNPJ - for corporations */
  CNPJ = 'CNPJ',
}

/** @enumType */
export type VatTypeWithLiterals = VatType | 'UNSPECIFIED' | 'CPF' | 'CNPJ';

export interface Address extends AddressStreetOneOf {
  /** Street name and number. */
  streetAddress?: StreetAddress;
  /** Main address line, usually street and number as free text. */
  addressLine1?: string | null;
  /**
   * Country code.
   * @format COUNTRY
   */
  country?: string | null;
  /** Subdivision. Usually a state, region, prefecture, or province code, according to [ISO 3166-2](https://en.wikipedia.org/wiki/ISO_3166-2). */
  subdivision?: string | null;
  /** City name. */
  city?: string | null;
  /** Zip/postal code. */
  postalCode?: string | null;
  /** Free text providing more detailed address info. Usually contains Apt, Suite, and Floor. */
  addressLine2?: string | null;
  /**
   * A string containing the full address of this location.
   * @internal
   */
  formatted?: string | null;
  /**
   * Free text to help find the address.
   * @internal
   */
  hint?: string | null;
  /**
   * Coordinates of the physical address.
   * @internal
   */
  location?: AddressLocation;
  /**
   * Country full name.
   * @internal
   */
  countryFullname?: string | null;
  /**
   * Subdivision full name.
   * @internal
   */
  subdivisionFullname?: string | null;
  /**
   * Multi-level subdivisions from top to bottom.
   * @internal
   * @maxSize 6
   */
  subdivisions?: Subdivision[];
}

/** @oneof */
export interface AddressStreetOneOf {
  /** Street name and number. */
  streetAddress?: StreetAddress;
  /** Main address line, usually street and number as free text. */
  addressLine?: string | null;
}

export interface StreetAddress {
  /** Street number. */
  number?: string;
  /** Street name. */
  name?: string;
  /**
   * Apartment number.
   * @internal
   */
  apt?: string;
  /**
   * Optional address line 1.
   * @internal
   */
  formattedAddressLine?: string | null;
}

export interface AddressLocation {
  /** Address latitude. */
  latitude?: number | null;
  /** Address longitude. */
  longitude?: number | null;
}

export interface Subdivision {
  /** Short subdivision code. */
  code?: string;
  /** Subdivision full name. */
  name?: string;
  /**
   * Subdivision level.
   * @internal
   */
  type?: SubdivisionTypeWithLiterals;
  /**
   * Free text description of subdivision type.
   * @internal
   */
  typeInfo?: string | null;
}

export enum SubdivisionType {
  UNKNOWN_SUBDIVISION_TYPE = 'UNKNOWN_SUBDIVISION_TYPE',
  /** State */
  ADMINISTRATIVE_AREA_LEVEL_1 = 'ADMINISTRATIVE_AREA_LEVEL_1',
  /** County */
  ADMINISTRATIVE_AREA_LEVEL_2 = 'ADMINISTRATIVE_AREA_LEVEL_2',
  /** City/town */
  ADMINISTRATIVE_AREA_LEVEL_3 = 'ADMINISTRATIVE_AREA_LEVEL_3',
  /** Neighborhood/quarter */
  ADMINISTRATIVE_AREA_LEVEL_4 = 'ADMINISTRATIVE_AREA_LEVEL_4',
  /** Street/block */
  ADMINISTRATIVE_AREA_LEVEL_5 = 'ADMINISTRATIVE_AREA_LEVEL_5',
  /** ADMINISTRATIVE_AREA_LEVEL_0. Indicates the national political entity, and is typically the highest order type returned by the Geocoder. */
  COUNTRY = 'COUNTRY',
}

/** @enumType */
export type SubdivisionTypeWithLiterals =
  | SubdivisionType
  | 'UNKNOWN_SUBDIVISION_TYPE'
  | 'ADMINISTRATIVE_AREA_LEVEL_1'
  | 'ADMINISTRATIVE_AREA_LEVEL_2'
  | 'ADMINISTRATIVE_AREA_LEVEL_3'
  | 'ADMINISTRATIVE_AREA_LEVEL_4'
  | 'ADMINISTRATIVE_AREA_LEVEL_5'
  | 'COUNTRY';

/** Payment method type identification. */
export enum PaymentMethod {
  UNKNOWN_METHOD_DATA = 'UNKNOWN_METHOD_DATA',
  /** Credit card. */
  CREDIT_CARD = 'CREDIT_CARD',
  /** Wallet. */
  WALLET = 'WALLET',
}

/** @enumType */
export type PaymentMethodWithLiterals =
  | PaymentMethod
  | 'UNKNOWN_METHOD_DATA'
  | 'CREDIT_CARD'
  | 'WALLET';

export interface CreditCardPaymentMethodData {
  /**
   * Card network.
   * @readonly
   * @minLength 1
   * @maxLength 100
   */
  network?: string;
  /**
   * Masked card number.
   * @readonly
   * @maxLength 100
   */
  maskedCreditCard?: string;
  /**
   * Card holder name.
   * @readonly
   * @maxLength 100
   */
  holderName?: string;
}

export interface WalletPaymentMethodData {
  /**
   * Buyer account email.
   * @format EMAIL
   * @readonly
   */
  buyerAccountEmail?: string | null;
}

export interface PaymentOrder extends PaymentOrderTypeDetailsOneOf {
  /** Wix eCommerce order details. */
  ecomOrder?: Ecom;
  /** Wix Cashier order details. */
  cashierOrder?: Cashier;
  /** Payment order type. */
  type?: OrderTypeWithLiterals;
}

/** @oneof */
export interface PaymentOrderTypeDetailsOneOf {
  /** Wix eCommerce order details. */
  ecomOrder?: Ecom;
  /** Wix Cashier order details. */
  cashierOrder?: Cashier;
}

export enum OrderType {
  UNKNOWN_ORDER_TYPE = 'UNKNOWN_ORDER_TYPE',
  /** Wix eCommerce order type. */
  ECOM = 'ECOM',
  /** Wix Cashier order type. */
  CASHIER = 'CASHIER',
}

/** @enumType */
export type OrderTypeWithLiterals =
  | OrderType
  | 'UNKNOWN_ORDER_TYPE'
  | 'ECOM'
  | 'CASHIER';

export interface Ecom {
  /**
   * Wix eCommerce order ID.
   * @format GUID
   * @readonly
   */
  ecomOrderId?: string;
}

export interface Cashier {
  /**
   * Wix Cashier payment order ID.
   * @format GUID
   * @readonly
   */
  cashierOrderId?: string;
}

export interface GiftCardPaymentLinkPayment {
  /**
   * Wix eCommerce order ID associated with the gift card payment.
   * @format GUID
   * @readonly
   */
  ecomOrderId?: string;
  /**
   * Buyer contact details.
   * @readonly
   */
  contactDetails?: FullAddressContactDetails;
  /**
   * Buyer contact ID, if relevant.
   * @format GUID
   * @readonly
   */
  contactId?: string | null;
  /**
   * Buyer's billing address.
   * @readonly
   */
  billingAddress?: Address;
}

export interface StandardCheckout {
  /**
   * Wix eCommerce checkout ID.
   * @format GUID
   * @readonly
   */
  ecomCheckoutId?: string;
}

export interface LeanCheckout {
  /**
   * Wix eCommerce order payment request ID.
   * @format GUID
   * @readonly
   */
  orderPaymentRequestId?: string;
}

export interface ExtendedFields {
  /**
   * Extended field data. Each key corresponds to the namespace of the app that created the extended fields.
   * The value of each key is structured according to the schema defined when the extended fields were configured.
   *
   * You can only access fields for which you have the appropriate permissions.
   *
   * Learn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).
   */
  namespaces?: Record<string, Record<string, any>>;
}

export interface DomainEvent extends DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  _id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at `16:00` and then again at `16:01`, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
}

/** @oneof */
export interface DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
}

export interface EntityCreatedEvent {
  entity?: string;
}

export interface RestoreInfo {
  deletedDate?: Date | null;
}

export interface EntityUpdatedEvent {
  /**
   * Since platformized APIs only expose PATCH and not PUT we can't assume that the fields sent from the client are the actual diff.
   * This means that to generate a list of changed fields (as opposed to sent fields) one needs to traverse both objects.
   * We don't want to impose this on all developers and so we leave this traversal to the notification recipients which need it.
   */
  currentEntity?: string;
}

export interface EntityDeletedEvent {
  /** Entity that was deleted. */
  deletedEntity?: string | null;
}

export interface ActionEvent {
  body?: string;
}

export interface MessageEnvelope {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Stringify payload. */
  data?: string;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface IdentificationData extends IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
  /** @readonly */
  identityType?: WebhookIdentityTypeWithLiterals;
}

/** @oneof */
export interface IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
}

export enum WebhookIdentityType {
  UNKNOWN = 'UNKNOWN',
  ANONYMOUS_VISITOR = 'ANONYMOUS_VISITOR',
  MEMBER = 'MEMBER',
  WIX_USER = 'WIX_USER',
  APP = 'APP',
}

/** @enumType */
export type WebhookIdentityTypeWithLiterals =
  | WebhookIdentityType
  | 'UNKNOWN'
  | 'ANONYMOUS_VISITOR'
  | 'MEMBER'
  | 'WIX_USER'
  | 'APP';

export interface EcomCheckout {
  /**
   * Wix eCommerce checkout ID.
   * @format GUID
   * @readonly
   */
  ecomCheckoutId?: string;
}

export interface EcomOrderCheckout {
  /**
   * Wix eCommerce order payment request ID.
   * @format GUID
   * @readonly
   */
  orderPaymentRequestId?: string;
}

export interface QueryPaymentLinkPaymentsRequest {
  /** Query options. */
  query: CursorQuery;
}

export interface CursorQuery extends CursorQueryPagingMethodOneOf {
  /**
   * Cursor paging options.
   *
   * Learn more about [cursor paging](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#cursor-paging).
   */
  cursorPaging?: CursorPaging;
  /**
   * Filter object.
   *
   * Learn more about [filtering](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#filters).
   */
  filter?: Record<string, any> | null;
  /**
   * Sort object.
   *
   * Learn more about [sorting](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#sorting).
   * @maxSize 5
   */
  sort?: Sorting[];
}

/** @oneof */
export interface CursorQueryPagingMethodOneOf {
  /**
   * Cursor paging options.
   *
   * Learn more about [cursor paging](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#cursor-paging).
   */
  cursorPaging?: CursorPaging;
}

export interface Sorting {
  /**
   * Name of the field to sort by.
   * @maxLength 512
   */
  fieldName?: string;
  /** Sort order. */
  order?: SortOrderWithLiterals;
}

export enum SortOrder {
  ASC = 'ASC',
  DESC = 'DESC',
}

/** @enumType */
export type SortOrderWithLiterals = SortOrder | 'ASC' | 'DESC';

export interface CursorPaging {
  /**
   * Maximum number of items to return in the results.
   * @max 100
   */
  limit?: number | null;
  /**
   * Pointer to the next or previous page in the list of results.
   *
   * Pass the relevant cursor token from the `pagingMetadata` object in the previous call's response.
   * Not relevant for the first request.
   * @maxLength 16000
   */
  cursor?: string | null;
}

export interface QueryPaymentLinkPaymentsResponse {
  /** Retrieved payment link payments. */
  paymentLinkPayments?: PaymentLinkPayment[];
  /** Paging metadata. */
  pagingMetadata?: CursorPagingMetadata;
}

export interface CursorPagingMetadata {
  /** Number of items returned in the response. */
  count?: number | null;
  /** Cursor strings that point to the next page, previous page, or both. */
  cursors?: Cursors;
  /**
   * Whether there are more pages to retrieve following the current page.
   *
   * + `true`: Another page of results can be retrieved.
   * + `false`: This is the last page.
   */
  hasNext?: boolean | null;
}

export interface Cursors {
  /**
   * Cursor string pointing to the next page in the list of results.
   * @maxLength 16000
   */
  next?: string | null;
  /**
   * Cursor pointing to the previous page in the list of results.
   * @maxLength 16000
   */
  prev?: string | null;
}

export interface SearchPaymentLinkPaymentsRequest {
  /** Search options. */
  search: CursorSearch;
}

export interface CursorSearch extends CursorSearchPagingMethodOneOf {
  /**
   * Cursor paging options.
   *
   * Learn more about [cursor paging](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#cursor-paging).
   */
  cursorPaging?: CursorPaging;
  /**
   * Filter object.
   *
   * Learn more about [filtering](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#filters).
   */
  filter?: Record<string, any> | null;
  /**
   * List of sort objects.
   *
   * Learn more about [sorting](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#sorting).
   * @maxSize 10
   */
  sort?: Sorting[];
  /**
   * Logical groupings of data into facets, with summaries for each facet. For example, use aggregations to allow site visitors to narrow down their search results by selecting specific categories.
   * @maxSize 10
   */
  aggregations?: Aggregation[];
  /** Free text to match in searchable fields. */
  search?: SearchDetails;
  /**
   * Time zone to adjust date-time-based filters and aggregations, in ISO 8601 (including offsets) or IANA time zone database (including time zone IDs) format.
   * Applies to all relevant filters and aggregations, unless overridden by providing timestamps including time zone. For example, "2023-12-20T10:52:34.795Z".
   * @maxLength 50
   */
  timeZone?: string | null;
}

/** @oneof */
export interface CursorSearchPagingMethodOneOf {
  /**
   * Cursor paging options.
   *
   * Learn more about [cursor paging](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#cursor-paging).
   */
  cursorPaging?: CursorPaging;
}

export interface Aggregation extends AggregationKindOneOf {
  /** A value aggregation calculates metrics such as count for specific fields within a dataset, providing insights into the overall distribution and key statistics of those values. For example, use a value aggregation to get the number (count) of products for each price listed in the store. */
  value?: ValueAggregation;
  /** A range aggregation calculates the count of the values from the specified field in the dataset that fall within the range of each bucket you define. For example, use a range aggregation to compare the number of reservations made for parties of 4 or less to the number of reservations made for parties with 5 or more. */
  range?: RangeAggregation;
  /** A scalar aggregation calculates a single numerical value from a dataset, summarizing the dataset into one key metric: `COUNT_DISTINCT`, `SUM`, `AVG`, `MIN`, or `MAX`. */
  scalar?: ScalarAggregation;
  /** A date histogram calculates the count of time values from the specified field in the dataset that fall within each time interval you define (hour, day, week, etc.) For example, use a date histogram to find how many reservations have been made at a restaurant each week. */
  dateHistogram?: DateHistogramAggregation;
  /** A nested aggregation is applied within the results of another aggregation. Rather than aggregating directly on the primary dataset, first group data using one aggregation and then apply another aggregation within each group. It allows for more complex analyses where you can summarize data at different levels of detail or hierarchy. For example, to get the number of products that are in stock and out of stock for each price listed, first perform a value aggregation on `discountedPriceNumeric`, and a second value aggregation on `inStock`. */
  nested?: NestedAggregation;
  /**
   * Aggregation name, returned in `aggregations.results.name`.
   * @maxLength 100
   */
  name?: string | null;
  /** Type of aggregation to perform. Must align with the corresponding aggregation field. */
  type?: AggregationTypeWithLiterals;
  /**
   * Field to aggregate by. Use dot notation to specify a JSON path. For example, `order.address.streetName`.
   * @maxLength 200
   */
  fieldPath?: string;
}

/** @oneof */
export interface AggregationKindOneOf {
  /** A value aggregation calculates metrics such as count for specific fields within a dataset, providing insights into the overall distribution and key statistics of those values. For example, use a value aggregation to get the number (count) of products for each price listed in the store. */
  value?: ValueAggregation;
  /** A range aggregation calculates the count of the values from the specified field in the dataset that fall within the range of each bucket you define. For example, use a range aggregation to compare the number of reservations made for parties of 4 or less to the number of reservations made for parties with 5 or more. */
  range?: RangeAggregation;
  /** A scalar aggregation calculates a single numerical value from a dataset, summarizing the dataset into one key metric: `COUNT_DISTINCT`, `SUM`, `AVG`, `MIN`, or `MAX`. */
  scalar?: ScalarAggregation;
  /** A date histogram calculates the count of time values from the specified field in the dataset that fall within each time interval you define (hour, day, week, etc.) For example, use a date histogram to find how many reservations have been made at a restaurant each week. */
  dateHistogram?: DateHistogramAggregation;
  /** A nested aggregation is applied within the results of another aggregation. Rather than aggregating directly on the primary dataset, first group data using one aggregation and then apply another aggregation within each group. It allows for more complex analyses where you can summarize data at different levels of detail or hierarchy. For example, to get the number of products that are in stock and out of stock for each price listed, first perform a value aggregation on `discountedPriceNumeric`, and a second value aggregation on `inStock`. */
  nested?: NestedAggregation;
}

export interface RangeBucket {
  /** Inclusive lower bound of the range. Required if `to` is not specified. */
  from?: number | null;
  /** Exclusive upper bound of the range. Required if `from` is not specified. */
  to?: number | null;
}

export enum SortType {
  /** Number of matches in the results. */
  COUNT = 'COUNT',
  /** Alphabetically by the field value. */
  VALUE = 'VALUE',
}

/** @enumType */
export type SortTypeWithLiterals = SortType | 'COUNT' | 'VALUE';

export enum SortDirection {
  /** Descending order. */
  DESC = 'DESC',
  /** Ascending order. */
  ASC = 'ASC',
}

/** @enumType */
export type SortDirectionWithLiterals = SortDirection | 'DESC' | 'ASC';

export enum MissingValues {
  /** Exclude missing values from the aggregation results. */
  EXCLUDE = 'EXCLUDE',
  /** Include missing values in the aggregation results. */
  INCLUDE = 'INCLUDE',
}

/** @enumType */
export type MissingValuesWithLiterals = MissingValues | 'EXCLUDE' | 'INCLUDE';

export interface IncludeMissingValuesOptions {
  /**
   * Specify a custom name for the bucket containing the missing values. Defaults are `"N/A"` for strings, `0` for integers, and `false` for booleans.
   * @maxLength 20
   */
  addToBucket?: string;
}

export enum ScalarType {
  /** Total number of distinct values. */
  COUNT_DISTINCT = 'COUNT_DISTINCT',
  /** Minimum value. */
  MIN = 'MIN',
  /** Maximum value. */
  MAX = 'MAX',
  /**
   * Sum of values.
   * @internal
   */
  SUM = 'SUM',
  /**
   * Average of values.
   * @internal
   */
  AVG = 'AVG',
}

/** @enumType */
export type ScalarTypeWithLiterals =
  | ScalarType
  | 'COUNT_DISTINCT'
  | 'MIN'
  | 'MAX';

export interface ValueAggregation extends ValueAggregationOptionsOneOf {
  /** Options for including missing values in results. */
  includeOptions?: IncludeMissingValuesOptions;
  /** Sort type. */
  sortType?: SortTypeWithLiterals;
  /** Sort direction. */
  sortDirection?: SortDirectionWithLiterals;
  /**
   * Number of aggregation results to return.
   * Min: `1`
   * Max: `250`
   * Default: `10`
   */
  limit?: number | null;
  /**
   * Whether to include or exclude missing values in the aggregation results.
   * Default: `EXCLUDE`.
   */
  missingValues?: MissingValuesWithLiterals;
}

/** @oneof */
export interface ValueAggregationOptionsOneOf {
  /** Options for including missing values in results. */
  includeOptions?: IncludeMissingValuesOptions;
}

export enum NestedAggregationType {
  /** Calculates the distribution of a specific field's values within a dataset, providing insights into the overall distribution and key statistics of those values. */
  VALUE = 'VALUE',
  /** Calculates the count of the values from the specified field in the dataset that fall within the range of each bucket you define. */
  RANGE = 'RANGE',
  /** Calculates a single numerical value from a dataset, summarizing the dataset into one key metric: `COUNT_DISTINCT`, `SUM`, `AVG`, `MIN`, or `MAX`. */
  SCALAR = 'SCALAR',
  /** Calculates the count of time values from the specified field in the dataset that fall within each time interval you define (hour, day, week, etc.). */
  DATE_HISTOGRAM = 'DATE_HISTOGRAM',
}

/** @enumType */
export type NestedAggregationTypeWithLiterals =
  | NestedAggregationType
  | 'VALUE'
  | 'RANGE'
  | 'SCALAR'
  | 'DATE_HISTOGRAM';

export interface RangeAggregation {
  /**
   * List of range buckets. During aggregation each entity is placed in the first bucket its value falls into, based on the provided range bounds.
   * @maxSize 50
   */
  buckets?: RangeBucket[];
}

export interface ScalarAggregation {
  /** Operator type for the scalar aggregation. */
  type?: ScalarTypeWithLiterals;
}

export interface DateHistogramAggregation {
  /** Interval for date histogram aggregation. */
  interval?: IntervalWithLiterals;
}

export enum Interval {
  YEAR = 'YEAR',
  MONTH = 'MONTH',
  WEEK = 'WEEK',
  DAY = 'DAY',
  HOUR = 'HOUR',
  MINUTE = 'MINUTE',
  SECOND = 'SECOND',
}

/** @enumType */
export type IntervalWithLiterals =
  | Interval
  | 'YEAR'
  | 'MONTH'
  | 'WEEK'
  | 'DAY'
  | 'HOUR'
  | 'MINUTE'
  | 'SECOND';

export interface NestedAggregationItem extends NestedAggregationItemKindOneOf {
  /** A value aggregation calculates the distribution of a specific field's values within a dataset, providing insights into the overall distribution and key statistics of those values. For example, use a value aggregation to get the number (count) of orders for each order status. */
  value?: ValueAggregation;
  /** A range aggregation calculates the count of the values from the specified field in the dataset that fall within the range of each bucket you define. For example, use a range aggregation to compare the number of reservations made for parties of 4 or less to the number of reservations made for parties with 5 or more. If ranges overlap, a record that fits more than one range will only be counted in the first range that matches the criteria. */
  range?: RangeAggregation;
  /** A scalar aggregation calculates a single numerical value from a dataset, summarizing the dataset into one key metric: `COUNT_DISTINCT`, `SUM`, `AVG`, `MIN`, or `MAX`. */
  scalar?: ScalarAggregation;
  /** A date histogram calculates the count of time values from the specified field in the dataset that fall within each time interval you define (hour, day, week, etc.). For example, use a date histogram to determine how many reservations have been made at a restaurant each week. If ranges overlap, a record that fits more than one range will only be counted in the first range that matches the criteria. */
  dateHistogram?: DateHistogramAggregation;
  /**
   * Unique, caller-defined aggregation name, returned in `aggregations.results`.
   * @maxLength 100
   */
  name?: string | null;
  /** Type of aggregation to perform. The matching aggregation field must be passed. */
  type?: NestedAggregationTypeWithLiterals;
  /**
   * Field to aggregate by. Use dot notation to specify a JSON path. For example, `order.address.streetName`.
   * @maxLength 200
   */
  fieldPath?: string;
}

/** @oneof */
export interface NestedAggregationItemKindOneOf {
  /** A value aggregation calculates the distribution of a specific field's values within a dataset, providing insights into the overall distribution and key statistics of those values. For example, use a value aggregation to get the number (count) of orders for each order status. */
  value?: ValueAggregation;
  /** A range aggregation calculates the count of the values from the specified field in the dataset that fall within the range of each bucket you define. For example, use a range aggregation to compare the number of reservations made for parties of 4 or less to the number of reservations made for parties with 5 or more. If ranges overlap, a record that fits more than one range will only be counted in the first range that matches the criteria. */
  range?: RangeAggregation;
  /** A scalar aggregation calculates a single numerical value from a dataset, summarizing the dataset into one key metric: `COUNT_DISTINCT`, `SUM`, `AVG`, `MIN`, or `MAX`. */
  scalar?: ScalarAggregation;
  /** A date histogram calculates the count of time values from the specified field in the dataset that fall within each time interval you define (hour, day, week, etc.). For example, use a date histogram to determine how many reservations have been made at a restaurant each week. If ranges overlap, a record that fits more than one range will only be counted in the first range that matches the criteria. */
  dateHistogram?: DateHistogramAggregation;
}

export enum AggregationType {
  /** Calculates the distribution of a specific field's values within a dataset, providing insights into the overall distribution and key statistics of those values. */
  VALUE = 'VALUE',
  /** Calculates the count of the values from the specified field in the dataset that fall within the range of each bucket you define. */
  RANGE = 'RANGE',
  /** Calculates a single numerical value from a dataset, summarizing the dataset into one key metric: `COUNT_DISTINCT`, `SUM`, `AVG`, `MIN`, or `MAX`. */
  SCALAR = 'SCALAR',
  /** Calculates the count of time values from the specified field in the dataset that fall within each time interval you define (hour, day, week, etc.). */
  DATE_HISTOGRAM = 'DATE_HISTOGRAM',
  /** Flattened list of aggregations, where each aggregation is nested within previous one. */
  NESTED = 'NESTED',
}

/** @enumType */
export type AggregationTypeWithLiterals =
  | AggregationType
  | 'VALUE'
  | 'RANGE'
  | 'SCALAR'
  | 'DATE_HISTOGRAM'
  | 'NESTED';

/** Nested aggregation expressed through a list of aggregation where each next aggregation is nested within previous one */
export interface NestedAggregation {
  /**
   * Flattened list of aggregations, where each next aggregation is nested within the previous one.
   * @minSize 2
   * @maxSize 3
   */
  nestedAggregations?: NestedAggregationItem[];
}

export interface SearchDetails {
  /** Search mode. Defines the search logic for combining multiple terms in the `expression`. */
  mode?: ModeWithLiterals;
  /**
   * Search term or expression.
   * @maxLength 100
   */
  expression?: string | null;
  /**
   * Fields to search in. If the array is empty, all searchable fields are searched. Use dot notation to specify a JSON path. For example, For example, `order.address.streetName`.
   * @maxLength 200
   * @maxSize 20
   */
  fields?: string[];
  /** Whether to enable the search function to use an algorithm to automatically find results that are close to the search expression, such as typos and declensions. */
  fuzzy?: boolean;
}

export enum Mode {
  /** Any of the search terms must be present. */
  OR = 'OR',
  /** All search terms must be present. */
  AND = 'AND',
}

/** @enumType */
export type ModeWithLiterals = Mode | 'OR' | 'AND';

export interface SearchPaymentLinkPaymentsResponse {
  /** Retrieved payment link payments. */
  paymentLinkPayments?: PaymentLinkPayment[];
  /** Paging metadata. */
  pagingMetadata?: PagingMetadataV2;
  /** Response aggregation data. */
  aggregationData?: AggregationData;
}

export interface PagingMetadataV2 {
  /** Number of items returned in the response. */
  count?: number | null;
  /** Offset that was requested. */
  offset?: number | null;
  /** Total number of items that match the query. Returned if offset paging is used and the `tooManyToCount` flag is not set. */
  total?: number | null;
  /** Flag that indicates the server failed to calculate the `total` field. */
  tooManyToCount?: boolean | null;
  /** Cursors to navigate through the result pages using `next` and `prev`. Returned if cursor paging is used. */
  cursors?: Cursors;
  /**
   * Indicates if there are more results after the current page.
   * If `true`, another page of results can be retrieved.
   * If `false`, this is the last page.
   * @internal
   */
  hasNext?: boolean | null;
}

export interface AggregationData {
  /**
   * List of the aggregated data results.
   * @maxSize 10000
   */
  results?: AggregationResults[];
}

export interface ValueAggregationResult {
  /**
   * Value of the field.
   * @maxLength 100
   */
  value?: string;
  /** Number of entities with this value. */
  count?: number;
}

export interface RangeAggregationResult {
  /** Inclusive lower bound of the range. */
  from?: number | null;
  /** Exclusive upper bound of the range. */
  to?: number | null;
  /** Total number of entities in this range. */
  count?: number;
}

export interface NestedAggregationResults
  extends NestedAggregationResultsResultOneOf {
  /** Value aggregation results. */
  values?: ValueResults;
  /** Range aggregation results. */
  ranges?: RangeResults;
  /** Scalar aggregation results. */
  scalar?: AggregationResultsScalarResult;
  /**
   * Unique, caller-defined aggregation name, identifiable by the requested aggregation `name`.
   * @maxLength 100
   */
  name?: string;
  /** Aggregation type. */
  type?: AggregationTypeWithLiterals;
  /**
   * Field which the data was aggregated by.
   * @maxLength 200
   */
  fieldPath?: string;
}

/** @oneof */
export interface NestedAggregationResultsResultOneOf {
  /** Value aggregation results. */
  values?: ValueResults;
  /** Range aggregation results. */
  ranges?: RangeResults;
  /** Scalar aggregation results. */
  scalar?: AggregationResultsScalarResult;
}

export interface ValueResults {
  /**
   * List of value aggregations.
   * @maxSize 250
   */
  results?: ValueAggregationResult[];
}

export interface RangeResults {
  /**
   * List of ranges returned in same order as requested.
   * @maxSize 50
   */
  results?: RangeAggregationResult[];
}

export interface AggregationResultsScalarResult {
  /** Type of scalar aggregation. */
  type?: ScalarTypeWithLiterals;
  /** Value of the scalar aggregation. */
  value?: number;
}

export interface NestedValueAggregationResult {
  /**
   * Value of the field.
   * @maxLength 1000
   */
  value?: string;
  /** Nested aggregations. */
  nestedResults?: NestedAggregationResults;
}

export interface ValueResult {
  /**
   * Value of the field.
   * @maxLength 1000
   */
  value?: string;
  /** Number of entities with this value. */
  count?: number | null;
}

export interface RangeResult {
  /** Inclusive lower bound of the range. */
  from?: number | null;
  /** Exclusive upper bound of the range. */
  to?: number | null;
  /** Number of entities in this range. */
  count?: number | null;
}

export interface ScalarResult {
  /** Value of the scalar aggregation. */
  value?: number;
}

export interface NestedResultValue extends NestedResultValueResultOneOf {
  /** Value aggregation result. */
  value?: ValueResult;
  /** Range aggregation result. */
  range?: RangeResult;
  /** Scalar aggregation result. */
  scalar?: ScalarResult;
  /** Date histogram aggregation result. */
  dateHistogram?: ValueResult;
}

/** @oneof */
export interface NestedResultValueResultOneOf {
  /** Value aggregation result. */
  value?: ValueResult;
  /** Range aggregation result. */
  range?: RangeResult;
  /** Scalar aggregation result. */
  scalar?: ScalarResult;
  /** Date histogram aggregation result. */
  dateHistogram?: ValueResult;
}

export interface Results {
  /** List of nested aggregations. */
  results?: Record<string, NestedResultValue>;
}

export interface DateHistogramResult {
  /**
   * Date in [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) format.
   * @maxLength 100
   */
  value?: string;
  /** Number of entities in the bucket. */
  count?: number;
}

export interface GroupByValueResults {
  /**
   * List of value aggregations.
   * @maxSize 1000
   */
  results?: NestedValueAggregationResult[];
}

export interface DateHistogramResults {
  /**
   * List of date histogram aggregations.
   * @maxSize 200
   */
  results?: DateHistogramResult[];
}

/**
 * Results of `NESTED` aggregation type in a flattened form
 * aggregations in resulting array are keyed by requested aggregation `name`.
 */
export interface NestedResults {
  /**
   * List of nested aggregations.
   * @maxSize 1000
   */
  results?: Results[];
}

export interface AggregationResults extends AggregationResultsResultOneOf {
  /** Value aggregation results. */
  values?: ValueResults;
  /** Range aggregation results. */
  ranges?: RangeResults;
  /** Scalar aggregation results. */
  scalar?: AggregationResultsScalarResult;
  /** Group by value aggregation results. */
  groupedByValue?: GroupByValueResults;
  /** Date histogram aggregation results. */
  dateHistogram?: DateHistogramResults;
  /** Nested aggregation results. */
  nested?: NestedResults;
  /**
   * Aggregation name, returned in `aggregations.results.name`.
   * @maxLength 100
   */
  name?: string;
  /** Aggregation type. Must align with the corresponding aggregation field. */
  type?: AggregationTypeWithLiterals;
  /**
   * Field to aggregate by. Use dot notation to specify a JSON path. For example, `order.address.streetName`.
   * @maxLength 200
   */
  fieldPath?: string;
}

/** @oneof */
export interface AggregationResultsResultOneOf {
  /** Value aggregation results. */
  values?: ValueResults;
  /** Range aggregation results. */
  ranges?: RangeResults;
  /** Scalar aggregation results. */
  scalar?: AggregationResultsScalarResult;
  /** Group by value aggregation results. */
  groupedByValue?: GroupByValueResults;
  /** Date histogram aggregation results. */
  dateHistogram?: DateHistogramResults;
  /** Nested aggregation results. */
  nested?: NestedResults;
}

export interface IssueReceiptRequest {
  /**
   * Payment link payment ID.
   * @format GUID
   */
  paymentLinkPaymentId: string | null;
}

export interface IssueReceiptResponse {
  /**
   * Issued receipt ID.
   * @format GUID
   */
  receiptId?: string;
}

export interface BulkUpsertPaymentLinkPaymentsRequest {
  /** Payment link payments to be created or updated. */
  paymentLinkPayments?: PaymentLinkPayment[];
}

export interface BulkUpsertPaymentLinkPaymentsResponse {
  /**
   * IDs of the created payment link payments.
   * @format GUID
   * @readonly
   */
  insertedIds?: string[] | null;
  /**
   * IDs of the updated payment link payments.
   * @format GUID
   * @readonly
   */
  updatedIds?: string[] | null;
  /**
   * IDs of the payment link payments that failed to be created or updated.
   * @format GUID
   * @readonly
   */
  failedIds?: string[] | null;
}

export interface Empty {}

export interface AccountInfo {
  /**
   * ID of the Wix account associated with the event.
   * @format GUID
   */
  accountId?: string | null;
  /**
   * ID of the parent Wix account. Only included when accountId belongs to a child account.
   * @format GUID
   */
  parentAccountId?: string | null;
  /**
   * ID of the Wix site associated with the event. Only included when the event is tied to a specific site.
   * @format GUID
   */
  siteId?: string | null;
}

/** @docsIgnore */
export type IssueReceiptApplicationErrors =
  | {
      code?: 'RECEIPT_NOT_FOUND';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'INVALID_PAYMENT_TYPE';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'INVALID_PAYMENT_DETAILS';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'INVALID_REGULAR_PAYMENT_LINK_PAYMENT_ORDER';
      description?: string;
      data?: Record<string, any>;
    };

export interface BaseEventMetadata {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface EventMetadata extends BaseEventMetadata {
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  _id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at `16:00` and then again at `16:01`, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
  accountInfo?: AccountInfoMetadata;
}

export interface AccountInfoMetadata {
  /** ID of the Wix account associated with the event */
  accountId: string;
  /** ID of the Wix site associated with the event. Only included when the event is tied to a specific site. */
  siteId?: string;
  /** ID of the parent Wix account. Only included when 'accountId' belongs to a child account. */
  parentAccountId?: string;
}

export interface PaymentLinkPaymentCreatedEnvelope {
  entity: PaymentLinkPayment;
  metadata: EventMetadata;
}

/**
 * Triggered when a payment link payment is created.
 * @permissionId PAYMENT_LINK_PAYMENTS.PAYMENT_LINK_PAYMENT_READ
 * @webhook
 * @eventType wix.paymentlinks.payments.v1.payment_link_payment_created
 * @slug created
 */
export declare function onPaymentLinkPaymentCreated(
  handler: (event: PaymentLinkPaymentCreatedEnvelope) => void | Promise<void>
): void;

export interface PaymentLinkPaymentUpdatedEnvelope {
  entity: PaymentLinkPayment;
  metadata: EventMetadata;
}

/**
 * Triggered when a payment link payment is updated.
 * @permissionId PAYMENT_LINK_PAYMENTS.PAYMENT_LINK_PAYMENT_READ
 * @webhook
 * @eventType wix.paymentlinks.payments.v1.payment_link_payment_updated
 * @slug updated
 */
export declare function onPaymentLinkPaymentUpdated(
  handler: (event: PaymentLinkPaymentUpdatedEnvelope) => void | Promise<void>
): void;

/**
 * Retrieves a list of up to 100 payment link payments given the specified paging, filtering, and sorting ([SDK](https://dev.wix.com/docs/sdk/articles/work-with-the-sdk/api-query-language) | [REST](https://dev.wix.com/api/rest/getting-started/api-query-language)).
 * @public
 * @permissionId PAYMENT_LINK_PAYMENTS.PAYMENT_LINK_PAYMENT_READ
 * @applicableIdentity APP
 * @fqn wix.paymentlinks.payments.v1.PaymentLinkPayments.QueryPaymentLinkPayments
 */
export function queryPaymentLinkPayments(): PaymentLinkPaymentsQueryBuilder {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[0] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  return queryBuilder<
    PaymentLinkPayment,
    'CURSOR',
    QueryPaymentLinkPaymentsRequest,
    QueryPaymentLinkPaymentsResponse
  >({
    func: async (payload: QueryPaymentLinkPaymentsRequest) => {
      const reqOpts =
        ambassadorWixPaymentlinksPaymentsV1PaymentLinkPayment.queryPaymentLinkPayments(
          payload
        );

      sideEffects?.onSiteCall?.();
      try {
        const result = await httpClient.request(reqOpts);
        sideEffects?.onSuccess?.(result);
        return result;
      } catch (err) {
        sideEffects?.onError?.(err);
        throw err;
      }
    },
    requestTransformer: (query: QueryPaymentLinkPaymentsRequest['query']) => {
      const args = [query, {}] as [
        QueryPaymentLinkPaymentsRequest['query'],
        {}
      ];
      return renameKeysFromSDKRequestToRESTRequest({
        ...args?.[1],
        query: args?.[0],
      });
    },
    responseTransformer: ({
      data,
    }: HttpResponse<QueryPaymentLinkPaymentsResponse>) => {
      const transformedData = renameKeysFromRESTResponseToSDKResponse(
        transformPaths(data, [
          {
            transformFn: transformRESTAddressToSDKAddress,
            paths: [
              {
                path: 'paymentLinkPayments.regularPaymentLinkPayment.billingAddress',
              },
              {
                path: 'paymentLinkPayments.giftCardPaymentLinkPayment.billingAddress',
              },
            ],
          },
        ])
      );

      return {
        items: transformedData?.paymentLinkPayments,
        pagingMetadata: transformedData?.pagingMetadata,
      };
    },
    errorTransformer: (err: unknown) => {
      const transformedError = sdkTransformError(err, {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { query: '$[0]' },
        singleArgumentUnchanged: false,
      });

      throw transformedError;
    },
    pagingMethod: 'CURSOR',
    transformationPaths: {},
  });
}

interface QueryCursorResult {
  cursors: Cursors;
  hasNext: () => boolean;
  hasPrev: () => boolean;
  length: number;
  pageSize: number;
}

export interface PaymentLinkPaymentsQueryResult extends QueryCursorResult {
  items: PaymentLinkPayment[];
  query: PaymentLinkPaymentsQueryBuilder;
  next: () => Promise<PaymentLinkPaymentsQueryResult>;
  prev: () => Promise<PaymentLinkPaymentsQueryResult>;
}

export interface PaymentLinkPaymentsQueryBuilder {
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  eq: (
    propertyName:
      | 'regularPaymentLinkPayment.paymentMethod'
      | 'regularPaymentLinkPayment.method'
      | '_createdDate'
      | 'paymentLinkId'
      | 'type'
      | 'transactionDate',
    value: any
  ) => PaymentLinkPaymentsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  ne: (
    propertyName:
      | 'regularPaymentLinkPayment.paymentMethod'
      | 'regularPaymentLinkPayment.method'
      | '_createdDate'
      | 'paymentLinkId'
      | 'type'
      | 'transactionDate',
    value: any
  ) => PaymentLinkPaymentsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  ge: (
    propertyName:
      | 'regularPaymentLinkPayment.paymentMethod'
      | '_createdDate'
      | 'paymentLinkId'
      | 'transactionDate',
    value: any
  ) => PaymentLinkPaymentsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  gt: (
    propertyName:
      | 'regularPaymentLinkPayment.paymentMethod'
      | '_createdDate'
      | 'paymentLinkId'
      | 'transactionDate',
    value: any
  ) => PaymentLinkPaymentsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  le: (
    propertyName:
      | 'regularPaymentLinkPayment.paymentMethod'
      | '_createdDate'
      | 'paymentLinkId'
      | 'transactionDate',
    value: any
  ) => PaymentLinkPaymentsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  lt: (
    propertyName:
      | 'regularPaymentLinkPayment.paymentMethod'
      | '_createdDate'
      | 'paymentLinkId'
      | 'transactionDate',
    value: any
  ) => PaymentLinkPaymentsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `string`.
   * @param string - String to compare against. Case-insensitive.
   */
  startsWith: (
    propertyName: 'regularPaymentLinkPayment.paymentMethod' | 'paymentLinkId',
    value: string
  ) => PaymentLinkPaymentsQueryBuilder;
  in: (
    propertyName:
      | 'regularPaymentLinkPayment.paymentMethod'
      | 'regularPaymentLinkPayment.method'
      | '_createdDate'
      | 'paymentLinkId'
      | 'type'
      | 'transactionDate',
    value: any
  ) => PaymentLinkPaymentsQueryBuilder;
  exists: (
    propertyName:
      | 'regularPaymentLinkPayment.paymentMethod'
      | 'regularPaymentLinkPayment.method'
      | '_createdDate'
      | 'paymentLinkId'
      | 'type'
      | 'transactionDate',
    value: boolean
  ) => PaymentLinkPaymentsQueryBuilder;
  /** @param propertyNames - Properties used in the sort. To sort by multiple properties, pass properties as additional arguments. */
  ascending: (
    ...propertyNames: Array<
      | 'regularPaymentLinkPayment.paymentMethod'
      | 'regularPaymentLinkPayment.method'
      | '_createdDate'
      | 'paymentLinkId'
      | 'type'
      | 'transactionDate'
    >
  ) => PaymentLinkPaymentsQueryBuilder;
  /** @param propertyNames - Properties used in the sort. To sort by multiple properties, pass properties as additional arguments. */
  descending: (
    ...propertyNames: Array<
      | 'regularPaymentLinkPayment.paymentMethod'
      | 'regularPaymentLinkPayment.method'
      | '_createdDate'
      | 'paymentLinkId'
      | 'type'
      | 'transactionDate'
    >
  ) => PaymentLinkPaymentsQueryBuilder;
  /** @param limit - Number of items to return, which is also the `pageSize` of the results object. */
  limit: (limit: number) => PaymentLinkPaymentsQueryBuilder;
  /** @param cursor - A pointer to specific record */
  skipTo: (cursor: string) => PaymentLinkPaymentsQueryBuilder;
  find: () => Promise<PaymentLinkPaymentsQueryResult>;
}

/**
 * @hidden
 * @fqn wix.paymentlinks.payments.v1.PaymentLinkPayments.QueryPaymentLinkPayments
 * @requiredField query
 */
export async function typedQueryPaymentLinkPayments(
  query: PaymentLinkPaymentQuery
): Promise<
  NonNullablePaths<
    QueryPaymentLinkPaymentsResponse,
    | `paymentLinkPayments`
    | `paymentLinkPayments.${number}.regularPaymentLinkPayment.creditCardPaymentMethodData.network`
    | `paymentLinkPayments.${number}.regularPaymentLinkPayment.creditCardPaymentMethodData.maskedCreditCard`
    | `paymentLinkPayments.${number}.regularPaymentLinkPayment.creditCardPaymentMethodData.holderName`
    | `paymentLinkPayments.${number}.regularPaymentLinkPayment.paymentMethod`
    | `paymentLinkPayments.${number}.regularPaymentLinkPayment.paymentProvider`
    | `paymentLinkPayments.${number}.regularPaymentLinkPayment.method`
    | `paymentLinkPayments.${number}.regularPaymentLinkPayment.order.type`
    | `paymentLinkPayments.${number}.giftCardPaymentLinkPayment.ecomOrderId`
    | `paymentLinkPayments.${number}.ecomCheckout.ecomCheckoutId`
    | `paymentLinkPayments.${number}.ecomOrderCheckout.orderPaymentRequestId`
    | `paymentLinkPayments.${number}.amount`
    | `paymentLinkPayments.${number}.paymentLinkId`
    | `paymentLinkPayments.${number}.type`
    | `paymentLinkPayments.${number}.currency`,
    6
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({ query: query });

  const reqOpts =
    ambassadorWixPaymentlinksPaymentsV1PaymentLinkPayment.queryPaymentLinkPayments(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTAddressToSDKAddress,
          paths: [
            {
              path: 'paymentLinkPayments.regularPaymentLinkPayment.billingAddress',
            },
            {
              path: 'paymentLinkPayments.giftCardPaymentLinkPayment.billingAddress',
            },
          ],
        },
      ])
    )!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { query: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['query']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface PaymentLinkPaymentQuerySpec extends QuerySpec {
  paging: 'cursor';
  wql: [
    {
      fields: [
        '_createdDate',
        'paymentLinkId',
        'regularPaymentLinkPayment.method',
        'regularPaymentLinkPayment.paymentMethod',
        'transactionDate',
        'type'
      ];
      operators: '*';
      sort: 'BOTH';
    }
  ];
}

export type CommonQueryWithEntityContext = QuerySdkType<
  PaymentLinkPayment,
  PaymentLinkPaymentQuerySpec
>;
export type PaymentLinkPaymentQuery = {
  /** 
  Cursor paging options.

  Learn more about [cursor paging](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#cursor-paging).  
  */
  cursorPaging?: {
    /** 
  Maximum number of items to return in the results. 
  @max: 100 
  */
    limit?:
      | NonNullable<CommonQueryWithEntityContext['cursorPaging']>['limit']
      | null;
    /** 
  Pointer to the next or previous page in the list of results.

  Pass the relevant cursor token from the `pagingMetadata` object in the previous call's response.
  Not relevant for the first request. 
  @maxLength: 16000 
  */
    cursor?:
      | NonNullable<CommonQueryWithEntityContext['cursorPaging']>['cursor']
      | null;
  };
  /** 
  Filter object.

  Learn more about [filtering](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#filters).  
  */
  filter?: CommonQueryWithEntityContext['filter'] | null;
  /** 
  Sort object.

  Learn more about [sorting](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#sorting). 
  @maxSize: 5 
  */
  sort?: {
    /** 
  Name of the field to sort by. 
  @maxLength: 512 
  */
    fieldName?: NonNullable<
      CommonQueryWithEntityContext['sort']
    >[number]['fieldName'];
    /** 
  Sort order.  
  */
    order?: NonNullable<CommonQueryWithEntityContext['sort']>[number]['order'];
  }[];
};

export const utils = {
  query: /*#__PURE__*/ createQueryUtils<
    PaymentLinkPayment,
    PaymentLinkPaymentQuerySpec,
    PaymentLinkPaymentQuery
  >(),
  search: /*#__PURE__*/ createSearchUtils<
    PaymentLinkPayment,
    PaymentLinkPaymentSearchSpec,
    PaymentLinkPaymentSearch
  >(),
};

/**
 * Retrieves a list of up to 100 payment links, given the specified filtering, search expression, sorting, and cursor paging ([SDK](https://dev.wix.com/docs/sdk/articles/work-with-the-sdk/api-query-language) | [REST](https://dev.wix.com/api/rest/getting-started/api-query-language)).
 * @param search - Search options.
 * @public
 * @requiredField search
 * @permissionId PAYMENT_LINK_PAYMENTS.PAYMENT_LINK_PAYMENT_READ
 * @applicableIdentity APP
 * @fqn wix.paymentlinks.payments.v1.PaymentLinkPayments.SearchPaymentLinkPayments
 */
export async function searchPaymentLinkPayments(
  search: PaymentLinkPaymentSearch
): Promise<
  NonNullablePaths<
    SearchPaymentLinkPaymentsResponse,
    | `paymentLinkPayments`
    | `paymentLinkPayments.${number}.regularPaymentLinkPayment.creditCardPaymentMethodData.network`
    | `paymentLinkPayments.${number}.regularPaymentLinkPayment.creditCardPaymentMethodData.maskedCreditCard`
    | `paymentLinkPayments.${number}.regularPaymentLinkPayment.creditCardPaymentMethodData.holderName`
    | `paymentLinkPayments.${number}.regularPaymentLinkPayment.paymentMethod`
    | `paymentLinkPayments.${number}.regularPaymentLinkPayment.paymentProvider`
    | `paymentLinkPayments.${number}.regularPaymentLinkPayment.method`
    | `paymentLinkPayments.${number}.regularPaymentLinkPayment.order.type`
    | `paymentLinkPayments.${number}.giftCardPaymentLinkPayment.ecomOrderId`
    | `paymentLinkPayments.${number}.ecomCheckout.ecomCheckoutId`
    | `paymentLinkPayments.${number}.ecomOrderCheckout.orderPaymentRequestId`
    | `paymentLinkPayments.${number}.amount`
    | `paymentLinkPayments.${number}.paymentLinkId`
    | `paymentLinkPayments.${number}.type`
    | `paymentLinkPayments.${number}.currency`
    | `aggregationData.results`
    | `aggregationData.results.${number}.scalar.type`
    | `aggregationData.results.${number}.scalar.value`
    | `aggregationData.results.${number}.name`
    | `aggregationData.results.${number}.type`
    | `aggregationData.results.${number}.fieldPath`,
    6
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({ search: search });

  const reqOpts =
    ambassadorWixPaymentlinksPaymentsV1PaymentLinkPayment.searchPaymentLinkPayments(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTAddressToSDKAddress,
          paths: [
            {
              path: 'paymentLinkPayments.regularPaymentLinkPayment.billingAddress',
            },
            {
              path: 'paymentLinkPayments.giftCardPaymentLinkPayment.billingAddress',
            },
          ],
        },
      ])
    )!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { search: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['search']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface PaymentLinkPaymentSearchSpec extends SearchSpec {
  searchable: [
    'giftCardPaymentLinkPayment.contactDetails.company',
    'giftCardPaymentLinkPayment.contactDetails.email',
    'giftCardPaymentLinkPayment.contactDetails.firstName',
    'giftCardPaymentLinkPayment.contactDetails.fullName',
    'giftCardPaymentLinkPayment.contactDetails.lastName',
    'regularPaymentLinkPayment.contactDetails.company',
    'regularPaymentLinkPayment.contactDetails.email',
    'regularPaymentLinkPayment.contactDetails.firstName',
    'regularPaymentLinkPayment.contactDetails.fullName',
    'regularPaymentLinkPayment.contactDetails.lastName'
  ];
  aggregatable: [
    '_createdDate',
    '_id',
    'amount',
    'giftCardPaymentLinkPayment.contactDetails.vatId._id',
    'giftCardPaymentLinkPayment.contactDetails.vatId.type',
    'giftCardPaymentLinkPayment.ecomOrderId',
    'paymentLinkId',
    'regularPaymentLinkPayment.contactDetails.vatId._id',
    'regularPaymentLinkPayment.contactDetails.vatId.type',
    'regularPaymentLinkPayment.method',
    'regularPaymentLinkPayment.order.ecomOrder.ecomOrderId',
    'regularPaymentLinkPayment.order.type',
    'regularPaymentLinkPayment.paymentMethod',
    'regularPaymentLinkPayment.paymentProvider',
    'transactionDate',
    'type'
  ];
  paging: 'cursor';
  wql: [
    {
      operators: '*';
      fields: [
        '_createdDate',
        'amount',
        'giftCardPaymentLinkPayment.contactDetails.vatId.type',
        'regularPaymentLinkPayment.contactDetails.vatId.type',
        'regularPaymentLinkPayment.method',
        'regularPaymentLinkPayment.order.type',
        'regularPaymentLinkPayment.paymentMethod',
        'regularPaymentLinkPayment.paymentProvider',
        'transactionDate',
        'type'
      ];
      sort: 'BOTH';
    },
    {
      operators: '*';
      fields: [
        '_id',
        'giftCardPaymentLinkPayment.contactDetails.vatId._id',
        'giftCardPaymentLinkPayment.ecomOrderId',
        'paymentLinkId',
        'regularPaymentLinkPayment.contactDetails.vatId._id',
        'regularPaymentLinkPayment.order.ecomOrder.ecomOrderId'
      ];
      sort: 'NONE';
    }
  ];
}

export type CommonSearchWithEntityContext = SearchSdkType<
  PaymentLinkPayment,
  PaymentLinkPaymentSearchSpec
>;
export type PaymentLinkPaymentSearch = {
  /** 
  Cursor paging options.

  Learn more about [cursor paging](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#cursor-paging).  
  */
  cursorPaging?: {
    /** 
  Maximum number of items to return in the results. 
  @max: 100 
  */
    limit?:
      | NonNullable<CommonSearchWithEntityContext['cursorPaging']>['limit']
      | null;
    /** 
  Pointer to the next or previous page in the list of results.

  Pass the relevant cursor token from the `pagingMetadata` object in the previous call's response.
  Not relevant for the first request. 
  @maxLength: 16000 
  */
    cursor?:
      | NonNullable<CommonSearchWithEntityContext['cursorPaging']>['cursor']
      | null;
  };
  /** 
  Filter object.

  Learn more about [filtering](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#filters).  
  */
  filter?: CommonSearchWithEntityContext['filter'] | null;
  /** 
  List of sort objects.

  Learn more about [sorting](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#sorting). 
  @maxSize: 10 
  */
  sort?: {
    /** 
  Name of the field to sort by. 
  @maxLength: 512 
  */
    fieldName?: NonNullable<
      CommonSearchWithEntityContext['sort']
    >[number]['fieldName'];
    /** 
  Sort order.  
  */
    order?: NonNullable<CommonSearchWithEntityContext['sort']>[number]['order'];
  }[];
  /** 
  Logical groupings of data into facets, with summaries for each facet. For example, use aggregations to allow site visitors to narrow down their search results by selecting specific categories. 
  @maxSize: 10 
  */
  aggregations?: {
    /** 
  A value aggregation calculates metrics such as count for specific fields within a dataset, providing insights into the overall distribution and key statistics of those values. For example, use a value aggregation to get the number (count) of products for each price listed in the store.  
  */
    value?: NonNullable<
      CommonSearchWithEntityContext['aggregations']
    >[number]['value'];
    /** 
  A range aggregation calculates the count of the values from the specified field in the dataset that fall within the range of each bucket you define. For example, use a range aggregation to compare the number of reservations made for parties of 4 or less to the number of reservations made for parties with 5 or more.  
  */
    range?: NonNullable<
      CommonSearchWithEntityContext['aggregations']
    >[number]['range'];
    /** 
  A scalar aggregation calculates a single numerical value from a dataset, summarizing the dataset into one key metric: `COUNT_DISTINCT`, `SUM`, `AVG`, `MIN`, or `MAX`.  
  */
    scalar?: NonNullable<
      CommonSearchWithEntityContext['aggregations']
    >[number]['scalar'];
    /** 
  A date histogram calculates the count of time values from the specified field in the dataset that fall within each time interval you define (hour, day, week, etc.) For example, use a date histogram to find how many reservations have been made at a restaurant each week.  
  */
    dateHistogram?: NonNullable<
      CommonSearchWithEntityContext['aggregations']
    >[number]['dateHistogram'];
    /** 
  A nested aggregation is applied within the results of another aggregation. Rather than aggregating directly on the primary dataset, first group data using one aggregation and then apply another aggregation within each group. It allows for more complex analyses where you can summarize data at different levels of detail or hierarchy. For example, to get the number of products that are in stock and out of stock for each price listed, first perform a value aggregation on `discountedPriceNumeric`, and a second value aggregation on `inStock`.  
  */
    nested?: NonNullable<
      CommonSearchWithEntityContext['aggregations']
    >[number]['nested'];
    /** 
  Aggregation name, returned in `aggregations.results.name`. 
  @maxLength: 100 
  */
    name?:
      | NonNullable<
          CommonSearchWithEntityContext['aggregations']
        >[number]['name']
      | null;
    /** 
  Type of aggregation to perform. Must align with the corresponding aggregation field.  
  */
    type?: NonNullable<
      CommonSearchWithEntityContext['aggregations']
    >[number]['type'];
    /** 
  Field to aggregate by. Use dot notation to specify a JSON path. For example, `order.address.streetName`. 
  @maxLength: 200 
  */
    fieldPath?: NonNullable<
      CommonSearchWithEntityContext['aggregations']
    >[number]['fieldPath'];
  }[];
  /** 
  Free text to match in searchable fields.  
  */
  search?: {
    /** 
  Search mode. Defines the search logic for combining multiple terms in the `expression`.  
  */
    mode?: NonNullable<CommonSearchWithEntityContext['search']>['mode'];
    /** 
  Search term or expression. 
  @maxLength: 100 
  */
    expression?:
      | NonNullable<CommonSearchWithEntityContext['search']>['expression']
      | null;
    /** 
  Fields to search in. If the array is empty, all searchable fields are searched. Use dot notation to specify a JSON path. For example, For example, `order.address.streetName`. 
  @maxLength: 200,
  @maxSize: 20 
  */
    fields?: NonNullable<CommonSearchWithEntityContext['search']>['fields'];
    /** 
  Whether to enable the search function to use an algorithm to automatically find results that are close to the search expression, such as typos and declensions.  
  */
    fuzzy?: NonNullable<CommonSearchWithEntityContext['search']>['fuzzy'];
  };
  /** 
  Time zone to adjust date-time-based filters and aggregations, in ISO 8601 (including offsets) or IANA time zone database (including time zone IDs) format.
  Applies to all relevant filters and aggregations, unless overridden by providing timestamps including time zone. For example, "2023-12-20T10:52:34.795Z". 
  @maxLength: 50 
  */
  timeZone?: CommonSearchWithEntityContext['timeZone'] | null;
};

/**
 * Issues a receipt for the specified payment link payment.
 * @param paymentLinkPaymentId - Payment link payment ID.
 * @public
 * @requiredField paymentLinkPaymentId
 * @permissionId PAYMENT_LINK_PAYMENTS.PAYMENT_LINK_PAYMENT_ISSUE_RECEIPT
 * @applicableIdentity APP
 * @fqn wix.paymentlinks.payments.v1.PaymentLinkPayments.IssueReceipt
 */
export async function issueReceipt(paymentLinkPaymentId: string): Promise<
  NonNullablePaths<IssueReceiptResponse, `receiptId`, 2> & {
    __applicationErrorsType?: IssueReceiptApplicationErrors;
  }
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    paymentLinkPaymentId: paymentLinkPaymentId,
  });

  const reqOpts =
    ambassadorWixPaymentlinksPaymentsV1PaymentLinkPayment.issueReceipt(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { paymentLinkPaymentId: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['paymentLinkPaymentId']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}
